import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV_SHORTCUTS = {
  '1': '/dashboard',
  '2': '/income',
  '3': '/expense',
  '4': '/sales',
  '5': '/purchase',
  '6': '/inventory',
  '7': '/ledger',
  '8': '/recurring',
};

const ADMIN_SHORTCUTS = {
  '9': '/audit-trail',
  '0': '/deleted-records',
};

const DATE_QUICK_MAP = {
  '1': 'current-month',
  '2': 'previous-month',
  '3': 'current-quarter',
  '4': 'previous-quarter',
  '5': 'current-fy',
  '6': 'previous-fy',
  '7': 'full-ledger',
};

const isInputElement = (target) => {
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
};

const handleEscKey = (navigate, location) => {
  if (window.__sectionTilesEscHandled && Date.now() - window.__sectionTilesEscHandled < 100) return true;
  if (document.querySelector('[role="dialog"]')) return true;

  // Smart back navigation: nested pages should go to their parent, not homepage.
  // Order matters: more specific routes first.
  const path = location?.pathname || '';
  const stateFrom = location?.state?.from;

  if (stateFrom) {
    navigate(stateFrom);
    return true;
  }
  if (path.startsWith('/sales/credit-notes')) {
    navigate('/sales');
    return true;
  }
  if (path.startsWith('/purchase/debit-notes')) {
    navigate('/purchase');
    return true;
  }
  // /ledger arrived from /sales or /purchase has tab in state — go back there.
  if (path === '/ledger') {
    const tab = location?.state?.tab;
    if (tab === 'customer-ledger') { navigate('/sales'); return true; }
    if (tab === 'supplier-ledger') { navigate('/purchase'); return true; }
  }
  navigate('/');
  return true;
};

const handleDateChord = (key, onDateQuick, onDateCustom) => {
  if (key === '8' && onDateCustom) { onDateCustom(); return true; }
  if (DATE_QUICK_MAP[key] && onDateQuick) { onDateQuick(DATE_QUICK_MAP[key]); return true; }
  return false;
};

const handleNavigation = (key, isAdmin, navigate) => {
  if (NAV_SHORTCUTS[key]) { navigate(NAV_SHORTCUTS[key]); return true; }
  if (isAdmin && ADMIN_SHORTCUTS[key]) { navigate(ADMIN_SHORTCUTS[key]); return true; }
  return false;
};

const handleFocusFilter = () => {
  const filter = document.querySelector('[data-shortcut-filter]');
  if (filter) {
    filter.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (filter.tagName === 'TR') {
      filter.dispatchEvent(new CustomEvent('shortcut-focus', { bubbles: true }));
    } else {
      filter.focus();
      filter.click();
    }
  }
};

export const useKeyboardShortcuts = ({ onNewEntry, onShowHelp, onPrevTab, onNextTab, onDateQuick, onDateCustom, onClearAction }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const waitingForD = useRef(false);
  const dTimeout = useRef(null);

  const handleKeyDown = useCallback((e) => {
    // Esc always works
    if (e.key === 'Escape') {
      e.preventDefault();
      handleEscKey(navigate, location);
      return;
    }

    if (isInputElement(e.target)) {
      waitingForD.current = false;
      return;
    }

    const isHomepage = location.pathname === '/';

    // Handle D+number chord (second key)
    if (waitingForD.current) {
      waitingForD.current = false;
      clearTimeout(dTimeout.current);
      if (handleDateChord(e.key, onDateQuick, onDateCustom)) {
        e.preventDefault();
        return;
      }
    }

    // D = Start date chord
    if ((e.key === 'd' || e.key === 'D') && !isHomepage && (onDateQuick || onDateCustom)) {
      e.preventDefault();
      waitingForD.current = true;
      clearTimeout(dTimeout.current);
      dTimeout.current = setTimeout(() => { waitingForD.current = false; }, 1500);
      return;
    }

    // Number keys: navigate ONLY from homepage
    if (isHomepage && handleNavigation(e.key, isAdmin, navigate)) {
      e.preventDefault();
      return;
    }

    // Tab switching with [ and ]
    if (!isHomepage && e.key === '[' && onPrevTab) { e.preventDefault(); onPrevTab(); return; }
    if (!isHomepage && e.key === ']' && onNextTab) { e.preventDefault(); onNextTab(); return; }

    // F = Focus filter
    if ((e.key === 'f' || e.key === 'F') && !isHomepage) { e.preventDefault(); handleFocusFilter(); return; }

    // H = Home
    if (e.key === 'h' || e.key === 'H') { e.preventDefault(); navigate('/'); return; }

    // N = New entry
    if ((e.key === 'n' || e.key === 'N') && onNewEntry) { e.preventDefault(); onNewEntry(); return; }

    // C = Clear / custom action
    if ((e.key === 'c' || e.key === 'C') && onClearAction) { e.preventDefault(); onClearAction(); return; }

    // ? = Show help
    if ((e.key === '?' || (e.key === '/' && e.shiftKey)) && onShowHelp) { e.preventDefault(); onShowHelp(); return; }
  }, [navigate, location.pathname, isAdmin, onNewEntry, onShowHelp, onPrevTab, onNextTab, onDateQuick, onDateCustom, onClearAction]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export const SHORTCUT_LIST = [
  { key: 'Esc', label: 'Close dialog / Go Home', section: 'General' },
  { key: 'H', label: 'Go Home', section: 'General' },
  { key: '?', label: 'Show shortcuts', section: 'General' },
  { key: '1-8', label: 'Open module (Home only)', section: 'Navigation' },
  { key: '9-0', label: 'Admin modules (Home only)', section: 'Navigation' },
  { key: '[', label: 'Previous tab', section: 'Page' },
  { key: ']', label: 'Next tab', section: 'Page' },
  { key: 'N', label: 'New / Add entry', section: 'Page' },
  { key: 'F', label: 'Focus filter field', section: 'Page' },
  { key: 'C', label: 'Clear logs (Audit Trail)', section: 'Page' },
  { key: 'D 1', label: 'This Month', section: 'Date Range' },
  { key: 'D 2', label: 'Last Month', section: 'Date Range' },
  { key: 'D 3', label: 'This Qtr', section: 'Date Range' },
  { key: 'D 4', label: 'Last Qtr', section: 'Date Range' },
  { key: 'D 5', label: 'This FY', section: 'Date Range' },
  { key: 'D 6', label: 'Last FY', section: 'Date Range' },
  { key: 'D 7', label: 'Full Ledger', section: 'Date Range' },
  { key: 'D 8', label: 'Custom dates', section: 'Date Range' },
  { key: '↑↓', label: 'Navigate ledgers (Entry Ledger)', section: 'Page' },
  { key: 'Enter', label: 'Open focused ledger / Next field / Submit', section: 'Page' },
  { key: 'Shift+Enter', label: 'New line in textarea', section: 'Forms' },
  { key: 'Alt+P', label: 'New Product (in PO form)', section: 'Forms' },
  { key: 'Alt+S', label: 'New Supplier (in PE form)', section: 'Forms' },
];
